import type { MachineLimits } from './types/factory';

/**
 * T-28: which fleet machine a task's run goes to, in this order:
 *
 * 0. SF-T-174: the machine a person chose for the task (task.ai_machine_id, with the AI builder on Waiting on
 *    or an @mention "AI builder on <machine>"). When it's busy or updating its agent, the task waits for it;
 *    when it can't run the project now (offline, revoked, its checkout gone or unconfirmed) or takes no AI
 *    builder runs, the rules below choose, and the run says why.
 * 1. The machine that worked on the task last (its newest run's, whatever that run's status), unless it
 *    can't run the project now (offline, revoked, its checkout gone or unconfirmed) or takes no AI builder
 *    runs (its limit is 0). When it's only busy or updating its agent, the task waits for it.
 * 2. (Run dialog only) the machine the person picked. The server takes it as given; this module only
 *    chooses the dialog's default.
 * 3. A machine the task's creator owns (machine.owner_user_id, set on the Fleet page) with the most free slots. When
 *    none has room, the task goes on to rule 4 rather than waiting.
 * 4. Any machine with room: one whose Claude weekly limit isn't nearly used up first, then (when the run
 *    reviews its PR, T-36) one that can review, then the most free slots; ties go to the first by name.
 *
 * SF-T-214: a machine that isn't set up for the project (its `not_ready`) is passed over under every rule, as
 * one that can't run the project; when no machine is ready, the task waits and says what each one lacks.
 *
 * Under rules 1 and 3 the machine is used even when it's near its Claude weekly limit. Either way, a run
 * on a machine at or past NEAR_LIMIT_PERCENT carries a warning, shown on the task.
 */

/** A Claude weekly limit at or past this is nearly used up. */
export const NEAR_LIMIT_PERCENT = 90;

export interface PickableMachine {
  id: string;
  name: string;
  /** T-27: a scheduled agent update: it takes no new runs until it restarts. */
  updating: boolean;
  max_active_runs: number;
  can_review: boolean;
  /** T-28: whose machine it is. */
  owner_user_id: string | null;
  /** T-28: its Claude weekly limit used, 0 to 100; null when it hasn't reported one. */
  claude_weekly_percent: number | null;
  /** SF-T-214: why it can't set up a worktree for the project (setup-readiness.ts); null or absent when it can. */
  not_ready?: string | null;
}

export type MachinePickRule = 'chosen' | 'previous' | 'owner' | 'any';

export interface MachinePickInput<M extends PickableMachine> {
  /** Machines that can run the project now, by name. */
  machines: readonly M[];
  /** Runs and chat turns going on each machine. */
  used: (machineId: string) => number;
  /** SF-T-174: the machine a person chose for the task, if any, and its name (for the reason when it can't run the project). */
  chosenMachineId?: string | null;
  chosenMachineName?: string | null;
  /** The machine of the task's newest run, if it has one. */
  previousMachineId: string | null;
  /** Who created the task. */
  creatorId: string | null;
  /** The creator's name, for the reason ("Deon's machine"). */
  creatorName?: string | null;
  /** The run reviews its PR (T-36): machines that can review come first under rule 4. */
  preferReview: boolean;
}

export type MachinePick<M extends PickableMachine> =
  | {
      kind: 'machine';
      machine: M;
      rule: MachinePickRule;
      /** Why this machine, e.g. "it worked on this task before". */
      why: string;
      /** Set when the machine is near its Claude weekly limit: a few words for the card and the full reason. */
      nearLimit: { short: string; reason: string } | null;
    }
  /** No machine for it now: why, as pickup-skips.ts keeps it. */
  | { kind: 'wait'; short: string; reason: string };

/** "2 of 2 slots used", or that the machine takes none (T-65: its limit is 0). */
export function slotsText(used: number, limit: number): string {
  return limit === 0 ? 'takes no AI builder runs' : `${used} of ${limit} slots used`;
}

/** Whether a machine's Claude weekly limit is nearly used up. */
export const isNearLimit = (m: Pick<PickableMachine, 'claude_weekly_percent'>) =>
  m.claude_weekly_percent !== null && m.claude_weekly_percent >= NEAR_LIMIT_PERCENT;

const percentText = (p: number) => `${Math.round(p)}%`;

/** The warning for a run on this machine when it's near its Claude weekly limit, else null. */
export function machineLimitWarning(m: Pick<PickableMachine, 'name' | 'claude_weekly_percent'>): { short: string; reason: string } | null {
  if (!isNearLimit(m)) return null;
  return { short: 'Near limit', reason: `${m.name} has used ${percentText(m.claude_weekly_percent!)} of its Claude weekly limit` };
}

/**
 * The fullest Claude weekly window in the machine's latest limits report (agent-devkit's limits.mjs labels
 * them "Weekly", "Weekly Opus"…), or null. A window whose reset has passed counts as 0: it has reset since.
 */
export function claudeWeeklyPercent(limits: MachineLimits | null | undefined, now = Date.now()): number | null {
  const claude = limits?.agents?.find((a) => a.id === 'claude' && !a.error);
  let best: number | null = null;
  for (const w of claude?.windows ?? []) {
    if (typeof w.usedPercent !== 'number' || !/^weekly/i.test(w.label ?? '')) continue;
    const reset = w.resetsAt ? Date.parse(w.resetsAt) : NaN;
    const used = Number.isFinite(reset) && reset <= now ? 0 : w.usedPercent;
    if (best === null || used > best) best = used;
  }
  return best;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

/**
 * SF-T-194: where the machine's Claude weekly limit is heading: the highest projection at reset of its Claude
 * weekly windows, 0 to 100 (or more: it would run out), or null when it can't be told (no limits reported, a
 * limits error, no weekly window). Each window's projection is the higher of the agent's own pace (`atReset`,
 * the last day's rate carried to the reset) and the week's average so far (used ÷ the share of the 7 days
 * gone, taking at least a day as gone so the first hours after a reset don't swing it). A window whose reset
 * has passed counts as 0, as claudeWeeklyPercent does. A weekly window with no usage figure makes it null.
 */
export function claudeWeeklyProjection(limits: MachineLimits | null | undefined, now = Date.now()): number | null {
  const claude = limits?.agents?.find((a) => a.id === 'claude' && !a.error);
  let best: number | null = null;
  for (const w of claude?.windows ?? []) {
    if (!/^weekly/i.test(w.label ?? '')) continue;
    // A weekly window with no figure could be full already: the trajectory can't be told.
    if (typeof w.usedPercent !== 'number' || !Number.isFinite(w.usedPercent)) return null;
    const reset = w.resetsAt ? Date.parse(w.resetsAt) : NaN;
    let projected: number | null;
    if (Number.isFinite(reset) && reset <= now) projected = 0;
    else {
      const pace = typeof w.atReset === 'number' && Number.isFinite(w.atReset) ? w.atReset : null;
      const gone = Number.isFinite(reset) ? Math.min(WEEK_MS, Math.max(DAY_MS, WEEK_MS - (reset - now))) : null;
      const average = gone !== null ? (w.usedPercent * WEEK_MS) / gone : null;
      projected = pace === null ? average : average === null ? pace : Math.max(pace, average);
    }
    // A window with no reset time and no pace can't be projected: the machine can't be called on track.
    if (projected === null) return null;
    if (best === null || projected > best) best = projected;
  }
  return best;
}

/**
 * SF-T-194: whether runs on this machine use Claude's fast mode: every Claude weekly window is projected to end
 * the week under NEAR_LIMIT_PERCENT. False when that can't be told.
 */
export const claudeFastOnTrack = (projected: number | null): boolean => projected !== null && projected < NEAR_LIMIT_PERCENT;

/**
 * SF-T-194: why runs on a machine use fast mode or not, as a clause: "dev-box is on track to use 62% of its
 * Claude weekly limit", "laptop is near its Claude weekly limit (on pace for 97% by the reset)", or that it
 * hasn't reported where it's heading.
 */
export function claudeTrajectoryText(name: string, projected: number | null): string {
  if (projected === null) return `${name} hasn't reported where its Claude weekly limit is heading`;
  if (claudeFastOnTrack(projected)) return `${name} is on track to use ${percentText(projected)} of its Claude weekly limit`;
  return `${name} is near its Claude weekly limit (on pace for ${percentText(projected)} by the reset)`;
}

/** The machine for a task's run, or why it waits. */
export function pickMachine<M extends PickableMachine>(input: MachinePickInput<M>): MachinePick<M> {
  // SF-T-214: only machines set up for the project; the ones that aren't say why when none is.
  const unready = input.machines.filter((m) => m.not_ready);
  if (!unready.length) return pickReady(input);
  const ready = input.machines.filter((m) => !m.not_ready);
  if (!ready.length) {
    const each = unready.map((m) => `${m.name} is ${m.not_ready}`).join('; ');
    return { kind: 'wait', short: 'Not set up', reason: `no machine is set up for this project: ${each}` };
  }
  const unreadyOne = (id: string | null | undefined) => (id ? unready.find((m) => m.id === id) : undefined);
  const chosen = unreadyOne(input.chosenMachineId);
  // The previous machine only matters when no machine was chosen for the task, or the chosen one is passed over.
  const previous = !input.chosenMachineId || chosen ? unreadyOne(input.previousMachineId) : undefined;
  const passed = chosen ?? previous;
  const pick = pickReady({
    ...input,
    machines: ready,
    chosenMachineId: chosen ? null : input.chosenMachineId,
    previousMachineId: unreadyOne(input.previousMachineId) ? null : input.previousMachineId,
  });
  if (!passed) return pick;
  const why = `${passed.name} is ${passed.not_ready}`;
  return pick.kind === 'machine' ? { ...pick, why: `${why}, and ${pick.why}` } : { ...pick, reason: `${why}; ${pick.reason}` };
}

/** pickMachine among machines set up for the project. */
function pickReady<M extends PickableMachine>(input: MachinePickInput<M>): MachinePick<M> {
  const { machines, used, chosenMachineId } = input;
  if (!chosenMachineId) return pickAutomatically(input);
  // 0. The machine chosen for the task, while it can run the project and takes AI builder runs at all.
  const chosen = machines.find((m) => m.id === chosenMachineId && m.max_active_runs > 0);
  if (chosen) {
    if (chosen.updating || used(chosen.id) >= chosen.max_active_runs) {
      const why = chosen.updating ? 'to finish updating its agent' : `to have room (${slotsText(used(chosen.id), chosen.max_active_runs)})`;
      return {
        kind: 'wait',
        short: chosen.updating ? `${chosen.name} updating` : `${chosen.name} full`,
        reason: `waiting for ${chosen.name}, chosen for this task, ${why}`,
      };
    }
    return { kind: 'machine', machine: chosen, rule: 'chosen', why: 'it was chosen for this task', nearLimit: machineLimitWarning(chosen) };
  }
  // It can't take the task now: the rules below choose, and say that it was passed over.
  const name = input.chosenMachineName || machines.find((m) => m.id === chosenMachineId)?.name || 'the machine chosen for this task';
  const passed = machines.some((m) => m.id === chosenMachineId) ? `${name} takes no AI builder runs` : `${name} can't run this project now (offline, or no confirmed checkout)`;
  const pick = pickAutomatically(input);
  return pick.kind === 'machine' ? { ...pick, why: `${passed}, and ${pick.why}` } : { ...pick, reason: `${passed}; ${pick.reason}` };
}

/** Rules 1 to 4: no machine chosen for the task, or that one can't run the project now. */
function pickAutomatically<M extends PickableMachine>(input: MachinePickInput<M>): MachinePick<M> {
  const { machines, used, previousMachineId, creatorId, preferReview } = input;
  const hasRoom = (m: M) => !m.updating && used(m.id) < m.max_active_runs;
  // Free slots against each machine's own limit (T-65), so 4 of 10 beats 1 of 2.
  const mostRoom = (a: M, b: M) => b.max_active_runs - used(b.id) - (a.max_active_runs - used(a.id));
  const warn = (m: M, all?: readonly M[]) => {
    if (!isNearLimit(m) || !all || all.length < 2) return machineLimitWarning(m);
    const each = all.map((x) => `${x.name} ${percentText(x.claude_weekly_percent!)}`).join(', ');
    return { short: 'Near limit', reason: `every machine with room is near its Claude weekly limit (${each})` };
  };

  // 1. The machine that worked on the task, while it can still run the project and takes AI builder
  // runs at all (a limit of 0 means none, so waiting for it would be forever).
  const previous = previousMachineId ? machines.find((m) => m.id === previousMachineId && m.max_active_runs > 0) : undefined;
  if (previous) {
    if (!hasRoom(previous)) {
      const why = previous.updating ? 'to finish updating its agent' : `to have room (${slotsText(used(previous.id), previous.max_active_runs)})`;
      return {
        kind: 'wait',
        short: previous.updating ? `${previous.name} updating` : `${previous.name} full`,
        reason: `waiting for ${previous.name}, which worked on this task before, ${why}`,
      };
    }
    return { kind: 'machine', machine: previous, rule: 'previous', why: 'it worked on this task before', nearLimit: warn(previous) };
  }

  // 3. The creator's own machine, when one has room.
  if (creatorId) {
    const own = machines.filter((m) => m.owner_user_id === creatorId && hasRoom(m)).sort(mostRoom)[0];
    if (own) {
      const whose = input.creatorName ? `${input.creatorName}'s machine` : "the task creator's machine";
      return { kind: 'machine', machine: own, rule: 'owner', why: `it's ${whose}`, nearLimit: warn(own) };
    }
  }

  // 4. Any machine with room.
  const open = machines.filter(hasRoom);
  const byLimit = (a: M, b: M) => Number(isNearLimit(a)) - Number(isNearLimit(b));
  const byReview = (a: M, b: M) => (preferReview ? Number(b.can_review) - Number(a.can_review) : 0);
  const [machine, next] = [...open].sort((a, b) => byLimit(a, b) || byReview(a, b) || mostRoom(a, b));
  if (machine) {
    // Why: the first criterion that put it ahead of the runner-up.
    const why = !next
      ? 'it was the only one with room'
      : byLimit(machine, next) < 0
        ? "it isn't near its Claude weekly limit"
        : byReview(machine, next) < 0
          ? 'it can review the PR'
          : mostRoom(machine, next) < 0
            ? 'it had the most free slots'
            : 'it had as many free slots as any';
    return { kind: 'machine', machine, rule: 'any', why, nearLimit: warn(machine, open) };
  }
  if (!machines.length) return { kind: 'wait', short: 'No machine', reason: 'no machine can run this project' };
  if (machines.every((m) => m.updating)) {
    return { kind: 'wait', short: 'Machines updating', reason: 'every machine that can run this project is updating its agent' };
  }
  const each = machines.map((m) => (m.updating ? `${m.name} updating` : `${m.name} ${slotsText(used(m.id), m.max_active_runs)}`));
  return { kind: 'wait', short: 'Machines busy', reason: `every machine is busy (${each.join(', ')})` };
}
